/**
 * RAILGUN first-sync phase profiler — TEMPORARY measurement tool.
 *
 * Goal: answer "where does the 10–15 min first-sync time actually go?" before we
 * decide whether a CDN-checkpoint / custom quick-sync is worth building. A checkpoint
 * only speeds up the *event-fetch* phase (quick-sync). It does NOT speed up the local
 * CPU phases (leaf hashing / note decryption / POI). So we need to see the breakdown.
 *
 * Coarse phases (always recorded, from the scan callbacks that are wired unconditionally
 * in railgunCore.ts):
 *   - refresh   : refreshBalances() call wall-time, per chain
 *   - utxoScan  : UTXO merkletree scan Started → Complete (quick-sync fetch + slow-sync
 *                 getLogs + leaf hashing + note decryption — all bundled by the SDK)
 *   - txidScan  : Railgun TXID merkletree scan Started → Complete
 *   - poiBatch  : first → last POI BatchListUpdateEvent
 *   - firstSpendable : refresh start → first Spendable balanceUpdate for the chain
 *
 * Fine sub-phases (only when RAILGUN_SCAN_DIAG === true, parsed from the SDK internal
 * logger forwarded via setLoggers): splits the bundled utxoScan into quick-sync vs
 * slow-sync(getLogs) vs leaf-scan(hashing) vs decrypt. Global (not per-chain) because
 * the SDK log lines don't reliably carry a chain id.
 *
 * Output: a per-chain table + a "wall-clock vs summed-CPU" line that reveals parallel
 * contention (running N chains at once does NOT divide CPU work by N — they fight over
 * one core, so wall-clock ≈ sum, not max).
 *
 * REMOVE once the measurement is done. Wired from railgunCore.ts (callbacks) and
 * railgunWallet.ts (getBalance).
 */

type PhaseName = 'refresh' | 'utxoScan' | 'txidScan' | 'poiBatch'

type Span = { start: number; end?: number; lastProgress?: number }

type ChainRecord = {
  name: string
  phases: Partial<Record<PhaseName, Span>>
  firstSpendableAt?: number
  bucketsSeen: Set<string>
}

// SDK-log tag → accumulated wall-time (ms) and hit count. Global across chains.
type LogTag = 'quickSync' | 'slowSync' | 'leafScan' | 'decrypt' | 'poi' | 'other'

const now = (): number => Date.now()

const fmt = (ms: number | undefined): string => {
  if (ms === undefined) return '—'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

class RailgunSyncProfiler {
  private chains = new Map<number, ChainRecord>()

  private sessionStart: number | undefined

  // SDK-log fine timeline: tag → { firstTs, lastTs, count }. Used to size sub-phases.
  private logTags = new Map<LogTag, { first: number; last: number; count: number }>()

  private lastLogTs: number | undefined

  /** Wipe state and start a fresh measurement window. */
  beginSession(): void {
    this.chains.clear()
    this.logTags.clear()
    this.lastLogTs = undefined
    this.sessionStart = now()
  }

  private record(chainId: number, name?: string): ChainRecord {
    let rec = this.chains.get(chainId)
    if (!rec) {
      rec = { name: name ?? String(chainId), phases: {}, bucketsSeen: new Set() }
      this.chains.set(chainId, rec)
    } else if (name && rec.name === String(chainId)) {
      rec.name = name
    }
    return rec
  }

  private openPhase(chainId: number, phase: PhaseName, name?: string): void {
    const rec = this.record(chainId, name)
    // Don't clobber an already-open span (Started can fire repeatedly).
    if (rec.phases[phase] && rec.phases[phase]!.end === undefined) return
    rec.phases[phase] = { start: now() }
  }

  private closePhase(chainId: number, phase: PhaseName): void {
    const span = this.record(chainId).phases[phase]
    if (span && span.end === undefined) span.end = now()
  }

  private touchPhase(chainId: number, phase: PhaseName, progress?: number): void {
    // For phases with no explicit Started (e.g. POI batches), open lazily on first sight.
    const rec = this.record(chainId)
    if (!rec.phases[phase]) rec.phases[phase] = { start: now() }
    rec.phases[phase]!.lastProgress = progress
    // Keep extending the end as long as events keep arriving (poiBatch has no Complete).
    rec.phases[phase]!.end = now()
  }

  refreshStart(chainId: number, name: string): void {
    if (this.sessionStart === undefined) this.beginSession()
    this.openPhase(chainId, 'refresh', name)
  }

  refreshEnd(chainId: number): void {
    this.closePhase(chainId, 'refresh')
  }

  utxoScan(evt: { chain: { id: number }; scanStatus: string; progress: number }): void {
    const id = evt.chain.id
    if (evt.scanStatus === 'Started') this.openPhase(id, 'utxoScan')
    else if (evt.scanStatus === 'Complete' || evt.scanStatus === 'Incomplete')
      this.closePhase(id, 'utxoScan')
    else this.touchPhase(id, 'utxoScan', evt.progress)
  }

  txidScan(evt: { chain: { id: number }; scanStatus: string; progress: number }): void {
    const id = evt.chain.id
    if (evt.scanStatus === 'Started') this.openPhase(id, 'txidScan')
    else if (evt.scanStatus === 'Complete' || evt.scanStatus === 'Incomplete')
      this.closePhase(id, 'txidScan')
    else this.touchPhase(id, 'txidScan', evt.progress)
  }

  poiBatch(evt: { chain?: { id: number } }): void {
    const id = evt?.chain?.id
    if (id === undefined) return
    this.touchPhase(id, 'poiBatch')
  }

  balanceBucket(chainId: number, bucket: string): void {
    const rec = this.record(chainId)
    rec.bucketsSeen.add(bucket)
    if (bucket === 'Spendable' && rec.firstSpendableAt === undefined) {
      rec.firstSpendableAt = now()
    }
  }

  /** Classify an SDK internal log line into a sub-phase bucket. RAILGUN_SCAN_DIAG only. */
  sdkLog(msg: string): void {
    const t = now()
    const tag = classifyLog(msg)
    const prev = this.logTags.get(tag)
    if (!prev) this.logTags.set(tag, { first: t, last: t, count: 1 })
    else {
      prev.last = t
      prev.count += 1
    }
    this.lastLogTs = t
  }

  printSummary(chainIds?: number[]): void {
    if (this.sessionStart === undefined) return
    const ids = chainIds ?? [...this.chains.keys()]
    const rows: Record<string, string>[] = []

    let minStart = Infinity
    let maxEnd = -Infinity
    let summedUtxo = 0
    let summedRefresh = 0

    for (const id of ids) {
      const rec = this.chains.get(id)
      if (!rec) continue
      const dur = (p: PhaseName): number | undefined => {
        const s = rec.phases[p]
        if (!s) return undefined
        return (s.end ?? now()) - s.start
      }
      const refresh = rec.phases.refresh
      if (refresh) {
        minStart = Math.min(minStart, refresh.start)
        maxEnd = Math.max(maxEnd, refresh.end ?? now())
        summedRefresh += (refresh.end ?? now()) - refresh.start
      }
      const u = dur('utxoScan')
      if (u !== undefined) summedUtxo += u
      const firstSpendable =
        rec.firstSpendableAt !== undefined && refresh
          ? rec.firstSpendableAt - refresh.start
          : undefined

      rows.push({
        chain: rec.name,
        refresh: fmt(dur('refresh')),
        utxoScan: fmt(u),
        txidScan: fmt(dur('txidScan')),
        poiBatch: fmt(dur('poiBatch')),
        '→1stSpendable': fmt(firstSpendable),
        buckets: [...rec.bucketsSeen].join(',') || '—'
      })
    }

    const wallClock = maxEnd > minStart ? maxEnd - minStart : undefined

    // eslint-disable-next-line no-console
    console.log('========== [RailgunSyncProfiler] first-sync breakdown ==========')
    // eslint-disable-next-line no-console
    if (typeof console.table === 'function') console.table(rows)
    // eslint-disable-next-line no-console
    else console.log(JSON.stringify(rows, null, 2))

    // eslint-disable-next-line no-console
    console.log('[RailgunSyncProfiler] parallel contention check:', {
      wallClock: fmt(wallClock),
      summedRefresh: fmt(summedRefresh),
      summedUtxoScan: fmt(summedUtxo),
      note:
        'wallClock ≈ summedRefresh → chains are CPU-serialized (parallelism not helping). ' +
        'wallClock ≪ summedRefresh → genuinely parallel.'
    })

    if (this.logTags.size > 0) {
      const tagRows = [...this.logTags.entries()].map(([tag, v]) => ({
        subPhase: tag,
        spanFromFirstSdkLog: fmt(v.last - this.sessionStart!),
        count: v.count
      }))
      // eslint-disable-next-line no-console
      console.log('[RailgunSyncProfiler] SDK-log sub-phase tags (utxoScan internals):')
      // eslint-disable-next-line no-console
      if (typeof console.table === 'function') console.table(tagRows)
      // eslint-disable-next-line no-console
      else console.log(JSON.stringify(tagRows, null, 2))
    } else {
      // eslint-disable-next-line no-console
      console.log(
        '[RailgunSyncProfiler] no SDK-log sub-phases captured — set RAILGUN_SCAN_DIAG=true ' +
          'in railgunCore.ts and rebuild to split utxoScan into quickSync/slowSync/decrypt.'
      )
    }
    // eslint-disable-next-line no-console
    console.log('================================================================')
  }
}

function classifyLog(msg: string): LogTag {
  const m = msg.toLowerCase()
  if (m.includes('quick sync') || m.includes('quicksync') || m.includes('graph')) return 'quickSync'
  if (m.includes('slow sync') || m.includes('slowsync') || m.includes('getlogs') || m.includes('eth_getlogs'))
    return 'slowSync'
  if (m.includes('scanning') || m.includes('leaves') || m.includes('merkletree') || m.includes('hash'))
    return 'leafScan'
  if (m.includes('decrypt') || m.includes('scanned') || m.includes('balance')) return 'decrypt'
  if (m.includes('poi') || m.includes('proof of innocence') || m.includes('batch')) return 'poi'
  return 'other'
}

export const syncProfiler = new RailgunSyncProfiler()
